import { resolveResourceURl } from "~/utils/url/urlResolver";
import { Trans, useTranslation } from "react-i18next";
import { useCommerceContext } from "~/app/config/useSession";

import {
  Breadcrumbs,
  Button,
  Collapse,
  Divider,
  Drawer,
  FormControl,
  FormLabel,
  IconButton,
  InputAdornment,
  Stack,
  styled,
  type Theme,
  TextField,
  type TextFieldProps,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  GlobalStyles,
} from "~/common/mui";
import {
  type FCCState,
  Hierarchy,
  Plant,
  Series,
  type TreeType,
  useFCCState,
} from "~/features/freeCapacityPush/useFCCState";
import debounce from "lodash/debounce";
import { forwardRef, useLayoutEffect, useMemo, useRef, useState } from "react";
import CenterContents from "~/common/components/CenterContents";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { FCCTree, type FCCTreeNode } from "~/features/freeCapacityPush/components/FCCTree/FCCTree";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import { LinkButton, type LinkButtonProps, LinkType } from "~/common/components/Link";
import { SearchResultFCC } from "~/features/freeCapacityPush/components/SearchResultFCC/SearchResultFCC";
import Pagination from "~/features/search/components/common/Pagination";
import { FreeCapacityTile } from "~/features/freeCapacityPush/components/FreeCapacityTile/FreeCapacityTile";
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from "chart.js";
import { SalesCoverageTile } from "~/features/freeCapacityPush/components/SalesCoverageTile/SalesCoverageTile";
import { NoSearchResults } from "~/features/search/components/common/NoSearchResults";
import { AvailabilityTile } from "~/features/freeCapacityPush/components/AvailabilityTile/AvailabilityTile";
import { ProductService } from "~/features/addToCart/api/ProductService";
import { getPackageCode } from "~/utils/cartHelper";
import { InfoCircle } from "~/common/components/InfoCircle/InfoCircle";
import { FccIntroTooltip } from "~/features/freeCapacityPush/components/FccIntroTooltip/FccIntroTooltip";
import { PopperTooltip } from "~/common/components/Tooltip";
import { SortingPicker } from "~/features/search/components/common/SortingPicker";
import { Menu, useSimpleMenuState } from "~/common/components/Menu/Menu";
import { MenuItem } from "~/common/components/Menu/MenuItem";
import { downloadFreeCapacityPushSalesCoverage, downloadFreeCapacityPushScope } from "./downloadFreeCapacityPushItems";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { Alert } from "~/common/mui/Alert/Alert";
import { Switch } from "~/common/components/Switch/Switch";
import TuneIcon from "@mui/icons-material/Tune";
import CloseIcon from "@mui/icons-material/Close";
import SearchOutlinedIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

const TABLET_HEADER_HEIGHT = 66;

const PageTitleRoot = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  gap: theme.spacing(1),
  alignItems: "center",
  h1: {
    margin: 0,
    ...theme.typography.headlineSmall,
    fontWeight: "bold",
  },
  [theme.breakpoints.down("phone")]: {
    ".page-title-info": {
      display: "none",
    },
  },
}));

const TooSmall = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(6),
}));

const TooSmallImage = styled("img")({
  aspectRatio: "248 / 190",
  width: 248,
  alignSelf: "center",
});

const Root = styled("div")(({ theme }) => ({
  width: "100vw",
  position: "relative",
  left: "50%",
  right: "50%",
  marginLeft: "-50vw",
  marginRight: "-50vw",
  "--column-amount": 4,
  "--fluid-container-margin": "16px",
  display: "grid",
  gridTemplateColumns:
    "calc(var(--fluid-container-margin) - var(--gutter)) repeat(var(--column-amount), minmax(0, 1fr)) calc(var(--fluid-container-margin) - var(--gutter))",
  columnGap: "var(--gutter)",
  "> *": { gridColumn: "2 / -2" },
  [theme.breakpoints.up("tablet")]: {
    "--column-amount": 6,
    "--fluid-container-margin": "24px",
  },
  [theme.breakpoints.up("desktop-s")]: {
    gridTemplateColumns: "1fr repeat(var(--column-amount), var(--column-width)) 1fr",
    "--column-amount": 12,
    "--column-width": "80px",
  },
  [theme.breakpoints.up("desktop-m")]: {
    "--column-width": "102px",
  },
  [theme.breakpoints.up("desktop-l")]: {
    "--column-width": "120px",
  },
}));

const InitialLoading = styled(CenterContents)(({ theme }) => ({
  marginTop: theme.spacing(8),
}));

const TabletHeader = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  padding: theme.spacing(3, 3, 2),
  gridColumn: "1 / -1",
  alignSelf: "flex-start",
  position: "sticky",
  top: 0,
  background: theme.palette.common.white,
  zIndex: 2,
  height: TABLET_HEADER_HEIGHT,
}));

const TabletButtons = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(3),
}));

const LeftSection = styled("div")(({ theme }) => ({
  [theme.breakpoints.up("desktop-s")]: {
    gridColumn: "2 / span 3",
    borderRight: `1px solid ${theme.palette.divider}`,
    paddingRight: "var(--gutter)",
  },
}));

const StickySideSection = styled("div")(({ theme }) => ({
  paddingTop: theme.spacing(3),
  [theme.breakpoints.up("desktop-s")]: {
    alignSelf: "start",
    position: "sticky",
    top: 0,
    maxHeight: "100vh",
    overflow: "auto",
    paddingBottom: theme.spacing(3),
    zIndex: 2,
  },
}));

const RightSection = styled("div")(({ theme }) => ({
  paddingLeft: theme.spacing(3),
  display: "grid",
  gridTemplateColumns: "subgrid",
  background: theme.palette.surface.main,
  gridColumn: "1 / -1",
  "> *": { gridColumn: "1 / 8" },
  [theme.breakpoints.down("tablet")]: {
    alignSelf: "start",
    position: "sticky",
    top: TABLET_HEADER_HEIGHT,
    overflow: "auto",
    paddingBottom: theme.spacing(3),
    zIndex: 2,
  },
  [theme.breakpoints.up("desktop-s")]: {
    gridColumn: "11 / span 4",
    "> *": { gridColumn: "1 / 4" },
  },
}));

const RightSectionInner = styled(StickySideSection)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  [theme.breakpoints.up("desktop-s")]: {
    gap: theme.spacing(3),
  },
}));

const Graphs = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  [theme.breakpoints.up("desktop-s")]: {
    gap: theme.spacing(2),
  },
}));

const graphCollapseSx = (theme: Theme) => ({
  "& .MuiCollapse-wrapperInner": {
    display: "grid",
    gap: theme.spacing(1),
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    [theme.breakpoints.up("desktop-s")]: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
    },
  },
});

const MiddleSection = styled("div")(({ theme }) => ({
  paddingTop: theme.spacing(3),
  [theme.breakpoints.up("desktop-s")]: {
    gridColumn: "5 / span 6",
    gridRow: 1,
    paddingTop: 0,
    "> *:first-of-type": {
      paddingTop: theme.spacing(3),
      background: theme.palette.common.white,
      zIndex: 2,
      position: "sticky",
      top: 0,
    },
  },
}));

const ResultsList = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
}));

const ResultsPagination = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(4),
}));

const ResultsLoading = styled("div")(({ theme }) => ({
  whiteSpace: "pre-line",
  textAlign: "center",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  alignItems: "center",
}));

const MiddleTitle = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  ...theme.typography.headlineSmall,
  marginBottom: theme.spacing(1),
}));

const QueryRow = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: theme.spacing(2),
}));

const StyledSortingPicker = styled(SortingPicker)(({ theme }) => ({
  marginTop: theme.spacing(2.5),
  flex: "0 1 30%",
}));

const MiddleDivider = styled(Divider)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(4),
}));

const OverviewHeaderContainer = styled("div")({
  display: "flex",
  justifyContent: "space-between",
});

const OverviewHeader = styled("h2")(({ theme }) => ({
  ...theme.typography.headlineSmall,
  margin: theme.spacing(0, 0, 1),
  [theme.breakpoints.up("desktop-s")]: {
    marginBottom: 0,
  },
}));

const SearchIcon = styled(SearchOutlinedIcon)(({ theme }) => ({
  color: theme.palette.primary.main,
}));

const AccountInfo = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
}));

const AccountInfoLabel = styled("span")(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginRight: theme.spacing(1),
}));

const DrawerContents = styled("section")(({ theme }) => ({
  padding: theme.spacing(0, 3),
  height: "100%",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  width: "50vw",
  maxWidth: 384,
}));

const DrawerHeader = styled("header")(({ theme }) => ({
  paddingTop: theme.spacing(3),
  position: "sticky",
  top: 0,
  background: theme.palette.common.white,
  zIndex: 1,
  ...theme.typography.headlineSmall,
}));

const DrawerTitle = styled("div")(({ theme }) => ({
  margin: 0,
  ...theme.typography.headlineSmall,
  display: "flex",
  justifyContent: "space-between",
}));

const DrawerClose = styled(IconButton)(({ theme }) => ({
  margin: theme.spacing(-1.5),
  alignSelf: "flex-start",
}));

const DrawerButtons = styled("footer")(({ theme }) => ({
  position: "sticky",
  background: theme.palette.common.white,
  zIndex: 1,
  bottom: 0,
  padding: theme.spacing(2),
  marginLeft: theme.spacing(-3),
  marginRight: theme.spacing(-3),
  borderTop: `1px solid ${theme.palette.divider}`,
  marginTop: "auto",
  display: "flex",
  gap: theme.spacing(3),
  "> *": { flex: 1 },
}));

const StyledFormLabel = styled(FormLabel)(({ theme }) => ({
  ...theme.typography.labelLarge,
  marginBottom: theme.spacing(0.75),
}));

const DownloadContainer = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  ...theme.typography.titleSmall,
}));

const DownloadLabel = styled("span")(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

const SmallSpinner = styled(CircleSpinner)({
  width: 16,
  height: 16,
});

function PageTitle({ className }: { className?: string }) {
  const { t } = useTranslation();

  return (
    <PageTitleRoot className={className}>
      <h1>{t("page.fcc.title")}</h1>
      <InfoCircle className="page-title-info" content={<FccIntroTooltip />} disableFlip={true} />
    </PageTitleRoot>
  );
}

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export const FreeCapacityCockpitPage = ({ downloadsEnabled = false }: { downloadsEnabled?: boolean }) => {
  const { t } = useTranslation();
  const screenSize = useNumericScreenSize();

  const { state, actions } = useFCCState();
  const [isDrawerOpen, setIsDrawerOpen] = useState(() => screenSize === useNumericScreenSize.tablet);
  const [isRenderDrawer, setRenderDrawer] = useState(() => screenSize === useNumericScreenSize.tablet);
  const [showExtraGraphs, setShowExtraGraphs] = useState(false);

  const openDrawer = () => {
    setIsDrawerOpen(true);
    setRenderDrawer(true);
  };

  const selectedProduct = ProductService.useNullableProductDescriptor({
    productCode: state.selectedProductCode ?? "",
    priceReference: "",
  });

  if (screenSize <= useNumericScreenSize.phone) {
    return (
      <TooSmall>
        <PageTitle />

        <TooSmallImage src={resolveResourceURl("/images/pcchan.svg")} />

        <Typography component="p" sx={{ m: 0 }}>
          {t("page.fcc.tooSmall")}
        </Typography>
      </TooSmall>
    );
  }

  if (!state.initialised) {
    return (
      <InitialLoading>
        <CircleSpinner />
      </InitialLoading>
    );
  }

  if (state.error) {
    return (
      <Root>
        <LeftSection>
          <StickySideSection>
            <PageTitle />
          </StickySideSection>
        </LeftSection>
        <MiddleSection>
          <Alert severity="error">{t("generic.error.ajax")}</Alert>
        </MiddleSection>
      </Root>
    );
  }

  const isDesktop = screenSize >= useNumericScreenSize.desktopS;

  const currentPageProducts = state.results[state.pagination.currentPage] ?? [];
  const showPagination = state.pagination.numberOfPages > 1;
  const showInsightsMessage = !state.selectedTreeNode && !state.selectedProductCode;

  let trainId = undefined;
  let breadcrumbs:
    | {
        code: string;
        name: string;
      }[]
    | undefined = undefined;
  let trainSelected = false;
  if (selectedProduct) {
    const packCode = getPackageCode(state.selectedFifteenDigit!);
    const fifteenDigitData = selectedProduct.base.fifteenDigit[packCode];
    breadcrumbs = fifteenDigitData?.fccBreadcrumb;
    trainId = breadcrumbs?.[breadcrumbs.length - 1]?.name;
    trainSelected = true;
  } else if (state.selectedTree === Plant && state.selectedTreeNode && state.selectedTreeNode.children.length === 0) {
    trainId = state.selectedTreeNode.label;
    breadcrumbs = makeBreadcrumbFromTree(state.filteredData[Plant], state.selectedTreeNode.key);
    trainSelected = true;
  }

  let resultsTitle;
  if (state.loading) {
    resultsTitle = t("searchPage.tabs.productNoCount");
  } else {
    resultsTitle = (
      <Trans i18nKey={"searchPage.tabs.product"} count={state.pagination.totalNumberOfResults}>
        Products (
        <span data-test-id={"results-product-amount"}>{{ count: state.pagination.totalNumberOfResults }}</span>)
      </Trans>
    );
  }

  let productLineLabel = undefined;
  if (state.selectedTree === Hierarchy && state.selectedTreeNode) {
    const productLineKey = "/" + state.selectedTreeNode.key.substring(1).split("/")[0];
    productLineLabel = state.filteredData[Hierarchy].find((node) => node.key === productLineKey)?.label;
  }

  return (
    <>
      <GlobalStyles
        styles={{
          ".page-freeCapacityCockpit #root_breadcrumbs": { display: "none" },
          ".page-freeCapacityCockpit header.scha__main-header.scha__main-header": { margin: 0 },
        }}
      />
      <Root>
        {/* Left side */}
        {!isDesktop && (
          <TabletHeader>
            <PageTitle />
            <TabletButtons>
              {downloadsEnabled && <FreeCapacityPushDownloads />}
              <LinkButton iconBefore={<TuneIcon />} onClick={openDrawer}>
                {t("notificationsPage.filters.title")}
              </LinkButton>
            </TabletButtons>
          </TabletHeader>
        )}
        {isDesktop && (
          <LeftSection>
            <StickySideSection>
              <Filters state={state} actions={actions} isDesktop={true} />
            </StickySideSection>
          </LeftSection>
        )}

        {/* Right side */}
        <RightSection>
          <RightSectionInner>
            <OverviewHeaderContainer>
              <OverviewHeader>{t("page.fcc.overview")}</OverviewHeader>
              {!isDesktop && (
                <LinkButton
                  color={"primary"}
                  onClick={() => setShowExtraGraphs((prev) => !prev)}
                  iconBefore={showExtraGraphs ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                >
                  {t("page.fcc.moreGraphsButton")}
                </LinkButton>
              )}
            </OverviewHeaderContainer>
            {(breadcrumbs?.length ?? 0) > 0 && (
              <Breadcrumbs maxItems={isDesktop ? 2 : undefined} aria-label="breadcrumb">
                {breadcrumbs?.map(({ code, name }, index) => (
                  <LinkButton
                    key={code}
                    variant={LinkType.SECONDARY}
                    onClick={() => {
                      const node = findTreeNode(state.filteredData[Plant], code);
                      if (node) {
                        actions.selectTreeNode(node, Plant);
                      }
                    }}
                  >
                    {index !== breadcrumbs!.length - 1 ? name : <b>{name}</b>}
                  </LinkButton>
                ))}
              </Breadcrumbs>
            )}
            {showInsightsMessage && <Alert severity={"info"}>{t("page.fcc.noFiltersMessage")}</Alert>}
            <Graphs>
              {state.loadingTrain && (
                <CenterContents>
                  <CircleSpinner />
                </CenterContents>
              )}
              {!state.loadingTrain && (
                <>
                  <FreeCapacityTile data={state.trainDetails} trainLabel={trainId} trainSelected={trainSelected} />
                  <Collapse in={showExtraGraphs || screenSize > useNumericScreenSize.tablet} sx={graphCollapseSx}>
                    <AvailabilityTile
                      product={selectedProduct}
                      packageCode={getPackageCode(state.selectedFifteenDigit ?? "")}
                    />
                    <SalesCoverageTile
                      salesCoverage={state.salesCoverage}
                      selectedProductCode={state.selectedProductCode ?? undefined}
                      productLineLabel={productLineLabel}
                      salesCoverageType={state.salesCoverageType}
                      salesCoverageUnit={state.salesCoverageUnit}
                      onSalesCoverageTypeChange={actions.updateSalesCoverageType}
                      onSalesCoverageUnitChange={actions.updateSalesCoverageUnit}
                    />
                  </Collapse>
                </>
              )}
            </Graphs>
          </RightSectionInner>
        </RightSection>

        {/* Center */}
        <MiddleSection>
          <div>
            <MiddleTitle id="resultsHeader">
              <div>{resultsTitle}</div>
              {isDesktop && downloadsEnabled && <FreeCapacityPushDownloads />}
            </MiddleTitle>

            <QueryRow>
              <DebouncedInput
                sx={{ maxWidth: 354 }}
                label={t("page.fcc.search.placeholder")}
                onChange={(e) => actions.updateSearchQuery(e.target.value)}
                value={state.searchQuery}
                helperText={t("page.fcc.search.helper")}
                disabled={state.loading}
                InputProps={{
                  endAdornment: state.searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton onClick={actions.clearSearchQuery} color={"primary"}>
                        <CloseIcon />
                      </IconButton>
                    </InputAdornment>
                  ) : (
                    <SearchIcon />
                  ),
                }}
              />

              <StyledSortingPicker
                options={state.sorts}
                selectedCode={state.selectedSort}
                selectedDirection={state.selectedSortDirection ?? null}
                onSelect={(newCode) => actions.updateSort(newCode)}
                onAscDesc={(newDirection) => actions.updateSort(state.selectedSort, newDirection)}
                useTranslationKeyForLabel={true}
              />
            </QueryRow>

            <MiddleDivider orientation={"horizontal"} />
          </div>

          <ResultsList>
            {state.loading && (
              <CenterContents>
                <ResultsLoading>
                  <CircleSpinner />
                  <Typography color={"textPrimary"}>{t("page.fcc.loadingResults")}</Typography>
                </ResultsLoading>
              </CenterContents>
            )}
            {currentPageProducts.length > 0 && (
              <div style={{ display: state.loading ? "none" : "contents" }}>
                {currentPageProducts.map((productCode) => (
                  <SearchResultFCC
                    key={productCode}
                    productCode={productCode}
                    searchQuery={state.searchQuery}
                    isSelected={state.selectedProductCode === productCode}
                    onSelect={(productCode, fifteenDigits) => {
                      actions.selectProduct(productCode, fifteenDigits);
                      setShowExtraGraphs(true);
                    }}
                  />
                ))}
              </div>
            )}
            {!state.loading && currentPageProducts.length === 0 && <NoSearchResults mode={"short"} />}
          </ResultsList>

          {!state.loading && showPagination && (
            <ResultsPagination>
              <Pagination
                classes={{ root: "scha__srp__pagination" }}
                pageAmount={state.pagination.numberOfPages}
                currentPage={state.pagination.currentPage}
                onPageChange={actions.changePage}
                renderHrefs={true}
                goToIdAfterChange={"#resultsHeader"}
              />
            </ResultsPagination>
          )}
        </MiddleSection>
      </Root>

      {!isDesktop && isRenderDrawer && (
        <SettingsDrawer
          open={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          initialState={state}
          onClear={actions.resetState}
          onSubmit={actions.updateState}
          onExited={() => setRenderDrawer(false)}
        />
      )}
    </>
  );
};

const DebouncedInput = ({ value, onChange, ...rest }: Omit<TextFieldProps, "ref">) => {
  const debouncedOnChange = useMemo(() => {
    return debounce(onChange ?? (() => {}), 500);
  }, [onChange]);
  const [internalValue, setInternalValue] = useState(value);
  useLayoutEffect(() => {
    setInternalValue(value);
  }, [value]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInternalValue(e.target.value);
    debouncedOnChange(e);
  };
  return <TextField fullWidth value={internalValue} onChange={handleChange} {...rest} />;
};

const AccountInformation = ({ className }: { className?: string }) => {
  const { t } = useTranslation();
  const account = useCommerceContext()?.selectedAccount;
  const name = account?.name ?? "N/A";
  const accountNumber = account?.displayUid ?? "N/A";
  return (
    <AccountInfo className={className}>
      <AccountInfoLabel>{t("page.fcc.account")}</AccountInfoLabel>
      <AccountNameText name={name} />
      <Divider orientation={"vertical"} flexItem />
      <span>{accountNumber}</span>
    </AccountInfo>
  );
};

const AccountName = ({ name, className }: { name: string; className?: string }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [addTooltip, setAddTooltip] = useState(false);
  useLayoutEffect(() => {
    if (ref.current) {
      setAddTooltip(ref.current.offsetWidth < ref.current.scrollWidth);
    }
  }, [name]);
  let el = (
    <span ref={ref} className={className}>
      {name}
    </span>
  );
  if (addTooltip) {
    el = (
      <PopperTooltip tooltipContent={name} className={className}>
        {el}
      </PopperTooltip>
    );
  }
  return el;
};

const AccountNameText = styled(AccountName)({
  textOverflow: "ellipsis",
  overflow: "hidden",
  whiteSpace: "nowrap",
});

interface SettingsDrawerProps {
  open: boolean;
  onClose: () => void;
  initialState: FCCState;
  onClear: () => void;
  onSubmit: (updatedSettings: FCCState) => void;
  onExited?: () => void;
}

const SettingsDrawer = ({ initialState, open, onClose, onClear, onSubmit, onExited }: SettingsDrawerProps) => {
  const { t } = useTranslation();
  const { state, actions } = useFCCState(true);
  useLayoutEffect(() => actions.updateState(initialState), [initialState]);
  return (
    <Drawer anchor={"right"} open={open} onClose={onClose} ModalProps={{ onTransitionExited: onExited }}>
      <DrawerContents>
        <DrawerHeader>
          <DrawerTitle>
            <p>{t("notificationsPage.settings.filter")}</p>
            <DrawerClose onClick={onClose} color={"primary"}>
              <CloseIcon />
            </DrawerClose>
          </DrawerTitle>
          <Divider orientation={"horizontal"} />
        </DrawerHeader>
        <Filters state={state} actions={actions} isDesktop={false} />
        <DrawerButtons>
          <Button
            size={"medium"}
            disabled={state.loading}
            onClick={() => {
              onClear();
              onClose();
            }}
            variant={"text"}
          >
            {t("page.linear.links.reset")}
          </Button>
          <Button
            disabled={state.loading}
            size={"medium"}
            onClick={() => {
              onSubmit(state);
              onClose();
            }}
          >
            {t("regionSelectionBanner.apply")}
          </Button>
        </DrawerButtons>
      </DrawerContents>
    </Drawer>
  );
};

const TREE_FILTER_LABELS: Record<TreeType, string> = {
  [Series]: "page.fcc.search.productSeries",
  [Hierarchy]: "page.fcc.search.productHierarchy",
  [Plant]: "page.fcc.search.plant",
};

const Filters = ({ state, actions, isDesktop }: ReturnType<typeof useFCCState> & { isDesktop: boolean }) => {
  const { t } = useTranslation();

  const treeData = state.filteredData[state.visibleTree];
  const isTreeEmpty = treeData.length === 0;

  const treeProps = {
    items: treeData,
    expanded: state.expanded[state.visibleTree],
    selected: state.selectedTreeNode?.key ?? null,
    setSelected: (node: FCCTreeNode | null) => actions.selectTreeNode(node, state.visibleTree),
    setExpanded: actions.setExpanded,
    onReset: isDesktop ? actions.resetTree : undefined,
  };

  const isNewSearchLoading = state.loading && state.pagination.currentPage === 0;

  const treeFilterLabel = t(TREE_FILTER_LABELS[state.visibleTree]);

  return (
    <Stack gap={3}>
      <Stack gap={2}>
        {isDesktop && <PageTitle />}
        {isDesktop && <Divider flexItem orientation={"horizontal"} />}
        <AccountInformation />
        <Stack gap={1} direction={"row"}>
          <Switch
            checked={state.showOnlyRelevantProducts}
            onChange={(_, value) => actions.toggleShowOnlyRelevantProducts(value)}
          />
          <span>{t("page.fcc.onlyRelevant.title")}</span>
          <InfoCircle
            content={
              <Typography className={"scha__popper-tooltip__inner"}>
                {t("page.fcc.onlyRelevant.description")}
              </Typography>
            }
          />
        </Stack>
      </Stack>
      <FormControl>
        <StyledFormLabel>{t("page.fcc.view")}</StyledFormLabel>
        <ToggleButtonGroup
          value={state.visibleTree}
          exclusive
          onChange={(_, value) => {
            if (!value) {
              return;
            }
            actions.changeVisibleTree(value as TreeType);
          }}
          fullWidth={true}
        >
          <ToggleButton value={Series}>{t("page.fcc.tree.series")}</ToggleButton>
          <ToggleButton value={Hierarchy}>{t("page.fcc.tree.hierarchy")}</ToggleButton>
          <ToggleButton value={Plant}>{t("page.fcc.tree.plant")}</ToggleButton>
        </ToggleButtonGroup>
      </FormControl>
      <div>
        <DebouncedInput
          label={treeFilterLabel}
          onChange={(e) => actions.updateTreeFilterQuery(e.target.value)}
          value={state.treeFilterQuery}
          disabled={state.loading}
          InputProps={{
            endAdornment: state.treeFilterQuery ? (
              <InputAdornment position="end">
                <IconButton onClick={actions.clearTreeFilterQuery} color={"primary"}>
                  <CloseIcon />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
        {isNewSearchLoading && (
          <CenterContents>
            <CircleSpinner />
          </CenterContents>
        )}
        {!isNewSearchLoading && isTreeEmpty && <span className={"title-large"}>{t("page.fcc.tree.noResults")}</span>}
        {!isNewSearchLoading && !isTreeEmpty && <FCCTree {...treeProps} searchTerm={state.treeFilterQuery} />}
      </div>
    </Stack>
  );
};

const DownloadClickLinkWithLoading = forwardRef<
  HTMLButtonElement,
  Omit<LinkButtonProps, "iconBefore"> & { loading?: boolean }
>((props, ref) => (
  <LinkButton
    disabled={props.loading}
    ref={ref}
    iconBefore={props.loading ? <SmallSpinner /> : <DownloadIcon />}
    {...props}
  />
));

const FreeCapacityPushDownloads = () => {
  const { t } = useTranslation();
  const [isOpen, handleClick, handleClose, anchorEl] = useSimpleMenuState();
  const { val: loading, setTrue: startLoading, setFalse: endLoading } = useBooleanState(false);
  return (
    <div>
      <DownloadContainer>
        <DownloadLabel>{`${t("productDetailsPage.tabs.downloads.tiles.button.download")}:`}</DownloadLabel>
        <DownloadClickLinkWithLoading loading={loading} className="title-small" onClick={handleClick}>
          {t("downloadFile.type.csv")}
        </DownloadClickLinkWithLoading>
      </DownloadContainer>

      <Menu
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={isOpen && !loading}
        onClose={handleClose}
        anchorEl={anchorEl}
      >
        <MenuItem
          onClick={async () => {
            startLoading();
            await downloadFreeCapacityPushScope();
            endLoading();
          }}
        >
          {t("quote.table.header.fcc.scope.title")}
        </MenuItem>
        <MenuItem
          onClick={async () => {
            startLoading();
            await downloadFreeCapacityPushSalesCoverage();
            endLoading();
          }}
        >
          {t("page.fcc.salesCoverage.download")}
        </MenuItem>
      </Menu>
    </div>
  );
};

/**
 * Create a breadcrumb from a tree and a selected node. The node can be at any level of the tree.
 * @param tree
 * @param selected
 */
function makeBreadcrumbFromTree(
  tree: FCCTreeNode[],
  selected: string | null,
): {
  code: string;
  name: string;
}[] {
  if (!selected) {
    return [];
  }
  for (const node of tree) {
    if (node.key === selected) {
      return [
        {
          code: node.key,
          name: node.label,
        },
      ];
    }
    const children = makeBreadcrumbFromTree(node.children, selected);
    if (children.length > 0) {
      return [
        {
          code: node.key,
          name: node.label,
        },
        ...children,
      ];
    }
  }
  return [];
}

function findTreeNode(tree: FCCTreeNode[], code: string): FCCTreeNode | null {
  for (const node of tree) {
    if (node.key === code) {
      return node;
    }
    const child = findTreeNode(node.children, code);
    if (child) {
      return child;
    }
  }
  return null;
}
